import z from 'zod';

import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import { DocumentSchema, type DocumentType } from './types';

export const RemoteDocumentSchema = DocumentSchema.and(z.object({ id: z.string() }));
export const GetStorageResponseSchema = z.object({
  resources: z.array(RemoteDocumentSchema),
});

const PostStorageResponseSchema = z.object({
  resource: RemoteDocumentSchema,
  clientId: z.string().optional(),
  id: z.string(),
});

const PutStorageResponseSchema = z.object({
  resource: RemoteDocumentSchema,
  id: z.string(),
});

const SuccessUploadReponseSchema = z.object({
  status: z.number().gt(200).lt(300),
  data: z.string().url(),
});

const SuccessDownloadReponseSchema = z.object({
  status: z.number().gt(200).lt(300),
  data: z.string().url(),
});

export default function buildStorageApi(domain: string) {
  return {
    async create(item: DocumentType & { clientId: string }, { signal }: { signal?: AbortSignal } = {}) {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document`;
      return PostStorageResponseSchema.parse(
        (await httpClientInterface.send('POST', requestUrl, item, undefined, undefined, { signal })).data,
      );
    },
    async fetch() {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document`;
      return GetStorageResponseSchema.parse((await httpClientInterface.send('GET', requestUrl)).data).resources;
    },
    async fetchById(id: string) {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document/${id}`;
      return RemoteDocumentSchema.parse((await httpClientInterface.send('GET', requestUrl)).data);
    },
    async update(item: DocumentType & { id: string }) {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document/${item.id}`;
      return PutStorageResponseSchema.parse((await httpClientInterface.send('PUT', requestUrl, item)).data);
    },
    async delete(id: string) {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document/${id}`;
      await httpClientInterface.send('DELETE', requestUrl);
    },

    async upload({
      fileUri,
      documentId,
      name,
      mimeType,
      signal,
    }: {
      fileUri: string;
      documentId: string;
      name: string;
      mimeType: string;
      signal?: AbortSignal;
    }) {
      const localFile = await fetch(fileUri, { signal });
      const fileBlob = await localFile.blob();
      const file = new File([fileBlob], name);
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document/${documentId}/upload`;
      const uploadUrl = SuccessUploadReponseSchema.parse(
        await httpClientInterface.send('PUT', requestUrl, undefined, undefined, undefined, { signal }),
      )?.data;
      // Use native fetch since axios has a polyfill issue on mobile https://github.com/axios/axios/issues/1321.
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: file,
        signal,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Storage document upload failed with status ${uploadResponse.status}.`);
      }
    },

    async getCdnUrl(id: string, useCdn: boolean) {
      const requestUrl = `${getServiceUrl()}/storage/${domain}/document/${id}/download?cdn=${useCdn}`;
      return SuccessDownloadReponseSchema.parse(await httpClientInterface.send('PUT', requestUrl)).data;
    },
  } as const;
}
